import { useMemo, useState, useCallback } from 'react';
import { buildMonerisIframeUrl } from '../../utils/monerisUrlBuilder';
import './MonerisIframe.css';

interface MonerisIframeProps {
    profileId: string;
    setIframeRef: (el: HTMLIFrameElement) => void;
    fieldErrors?: Record<string, string>;
}

function MonerisIframe({ profileId, setIframeRef, fieldErrors = {} }: MonerisIframeProps) {
    const [isLoading, setIsLoading] = useState(true);

    const iframeSrc = useMemo(() => {
        return buildMonerisIframeUrl(profileId);
    }, [profileId]);

    const handleRef = useCallback(
        (el: HTMLIFrameElement | null) => {
            if (el) {
                setIframeRef(el);
            }
        },
        [setIframeRef]
    );

    const handleLoad = useCallback(() => {
        setIsLoading(false);
    }, []);

    const hasErrors = Object.keys(fieldErrors).length > 0;
    const wrapperClass = hasErrors ? 'iframeWrapperError' : 'iframeWrapper';

    return (
        <section className="moneris-container">
            <h2 className="moneris-heading">Payment Details</h2>
            <p className="moneris-subheading">Enter your card information below</p>

            <div className={wrapperClass}>
                {isLoading && (
                    <div className="loading">
                        <span className="loadingSpinner" aria-hidden="true" />
                        <span>Loading secure payment form...</span>
                    </div>
                )}

                <iframe
                    id="monerisFrame"
                    ref={handleRef}
                    src={iframeSrc}
                    title="Moneris Secure Card Entry"
                    className="moneris-iframe"
                    onLoad={handleLoad}
                    style={{ display: isLoading ? 'none' : 'block' }}
                />
            </div>

            {hasErrors && (
                <div className="fieldErrors" role="alert">
                    {fieldErrors.cardNumber && (
                        <div className="fieldError">
                            <span className="fieldErrorDot" />
                            {fieldErrors.cardNumber}
                        </div>
                    )}
                    {fieldErrors.expiry && (
                        <div className="fieldError">
                            <span className="fieldErrorDot" />
                            {fieldErrors.expiry}
                        </div>
                    )}
                    {fieldErrors.cvd && (
                        <div className="fieldError">
                            <span className="fieldErrorDot" />
                            {fieldErrors.cvd}
                        </div>
                    )}
                    {fieldErrors.general && (
                        <div className="fieldError">
                            <span className="fieldErrorDot" />
                            {fieldErrors.general}
                        </div>
                    )}
                </div>
            )}

            <div className="moneris-footer">
                <div className="secureNote">
                    &#128274; Secured by Moneris
                </div>
                <div className="cardTypes">
                    Visa &middot; Mastercard &middot; Amex
                </div>
            </div>
        </section>
    );
}

export default MonerisIframe;
